import React from 'react';
import classNames from 'classnames';
import { isString } from 'lodash';
import PropTypes from 'prop-types';
import { getImageUrl } from '../../../../services';
import { isIos } from '../../../../services/detect-platform';
import { createHashtagPath, createHashtagHref, } from '../../../../services/search-page-url';
import linkSettings from '../../constants/linkSettings';
import { RCE_FILE_DOWNLOAD_WILL_BEGIN_SHORTLY } from '../../constants/messages';
import getStyle from '../../services/get-style';
import { viewerCustomStyleFn, viewerCustomBackgroundStyleFn, styleSelectionPredicate, getTextColorSchema, } from '../../services/text-color-utils';
import { createRicosTheme } from '../../services/themeRicos';
import styles from './ricos-viewer-wrapper.scss';
const MAX_PINTEREST_IMAGE_SIZE = 5000;
class RicosViewer extends React.PureComponent {
    constructor(props) {
        super(props);
        this.state = {
            error: false,
        };
        this.getImagePluginConfig = () => {
            return {
                imageProps: (src) => ({
                    'data-pin-url': this.props.pageUrl,
                    'data-pin-media': isString(src)
                        ? src
                        : getImageUrl({
                            image: src,
                            maxWidth: MAX_PINTEREST_IMAGE_SIZE,
                            maxHeight: MAX_PINTEREST_IMAGE_SIZE,
                        }),
                }),
            };
        };
        this.getGalleryPluginConfig = () => {
            const { isInEditor, Wix } = this.props;
            const scrollingElementStub = {
                addEventListener: (eventName, callback) => {
                    Wix.addEventListener(eventName.toUpperCase(), callback);
                },
                removeEventListener: (eventName, callback) => {
                    Wix.removeEventListener(eventName.toUpperCase(), callback);
                },
            };
            return {
                imageProps: () => ({ 'data-pin-url': this.props.pageUrl }),
                scrollingElement: isInEditor
                    ? scrollingElementStub
                    : typeof window !== 'undefined' && window,
            };
        };
        this.getTextColorPluginConfig = () => {
            const { appSettings, isHexColor } = this.props;
            const colorScheme = getTextColorSchema({ appSettings });
            return {
                customStyleFn: viewerCustomStyleFn(colorScheme, { isHexColor }),
                styleSelectionPredicate: styleSelectionPredicate(colorScheme, {
                    isHexColor,
                }),
            };
        };
        this.getTextHighlightPluginConfig = () => {
            const { appSettings, isHexColor } = this.props;
            const colorScheme = getTextColorSchema({ appSettings });
            return {
                customStyleFn: viewerCustomBackgroundStyleFn(colorScheme, { isHexColor }),
                styleSelectionPredicate: styleSelectionPredicate(colorScheme, {
                    isHexColor,
                }),
            };
        };
        this.getLinkPreviewPluginConfig = () => {
            return {
                enableEmbed: true,
                enableLinkPreview: false,
            };
        };
        this.getHashtagPluginConfig = () => {
            const { hashtagNavigate, sectionUrl } = this.props;
            return {
                onClick: (event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    hashtagNavigate(createHashtagPath(event.target.innerText, {
                        newPath: this.props.isHashtagPageAsFeedPageEnabled,
                    }));
                },
                createHref: (decoratedText) => createHashtagHref(decoratedText, sectionUrl, {
                    newPath: this.props.isHashtagPageAsFeedPageEnabled,
                }),
            };
        };
        this.getActionButtonPluginConfig = () => ({ onClick: this.props.onButtonClick });
        this.getFileDownloadBIProps = (file, success) => {
            const { postId, isDemoPost } = this.props;
            return {
                file_ext: file.type,
                file_id: file.id,
                file_size: file.size,
                mime_type: file.mimeType,
                post_stable_id: postId,
                is_demo: isDemoPost,
                origin: 'post',
                success,
            };
        };
        this.getFilePluginConfig = () => {
            const { postId, bi, actions, metasiteId, instance, resolveFileUrl } = this.props;
            return {
                resolveFileUrl: (file) => {
                    isIos() && actions.showMessage(RCE_FILE_DOWNLOAD_WILL_BEGIN_SHORTLY);
                    if (resolveFileUrl) {
                        return resolveFileUrl(metasiteId, instance)(file)
                            .then((url) => {
                            bi.fileDownloaded(this.getFileDownloadBIProps(file, true));
                            return url;
                        })
                            .catch(() => bi.fileDownloaded(this.getFileDownloadBIProps(file, false)));
                    }
                    return actions
                        .requestFileDownloadUrl(postId, file.path, file.id, file.mimeType)
                        .then(({ url }) => {
                        bi.fileDownloaded(this.getFileDownloadBIProps(file, true));
                        return url;
                    })
                        .catch(() => bi.fileDownloaded(this.getFileDownloadBIProps(file, false)));
                },
            };
        };
        this.getPollSettings = () => {
            const onBeforeVote = ({ voteRole }) => {
                const { currentUser, actions, isBusinessManager } = this.props;
                return new Promise((resolve, reject) => {
                    if (isBusinessManager) {
                        return reject();
                    }
                    if (currentUser || voteRole === 'ALL') {
                        return resolve();
                    }
                    actions.requestLogin().then(resolve, reject);
                });
            };
            return { onBeforeVote };
        };
        this.getPlugins = () => {
            const plugins = [
                this.props.pluginAudio(),
                this.props.pluginCodeBlock(),
                this.props.pluginDivider(),
                this.props.pluginLinkButton(),
                this.props.pluginGallery(this.getGalleryPluginConfig()),
                this.props.pluginGiphy(),
                this.props.pluginHashtag(this.getHashtagPluginConfig()),
                this.props.pluginHtml(),
                this.props.pluginImage(this.getImagePluginConfig()),
                this.props.pluginLineSpacing(),
                this.props.pluginIndent(),
                this.props.pluginLink(),
                ...(this.props.isRicosEmbedButtonsEnabled
                    ? [this.props.pluginLinkPreview(this.getLinkPreviewPluginConfig())]
                    : []),
                this.props.pluginTextColor(this.getTextColorPluginConfig()),
                this.props.pluginTextHighlight(this.getTextHighlightPluginConfig()),
                this.props.pluginVideo(),
                this.props.pluginFileUpload(this.getFilePluginConfig()),
                this.props.pluginActionButton(this.getActionButtonPluginConfig()),
                this.props.pluginVerticalEmbed(),
                this.props.pluginTable(),
                this.props.pluginCollapsibleList(),
                ...(this.props.pluginPoll
                    ? [this.props.pluginPoll(this.getPollSettings())]
                    : []),
            ];
            return plugins;
        };
        this.onError = (error) => {
            if (!this.state.error) {
                this.setState({ error });
            }
        };
        // TODO: remove when ricos supports plain object
        this.wixExperiments = {
            all: () => props.experiments,
        };
    }
    render() {
        if (this.state.error) {
            return this.props.fallback();
        }
        const { WixRicosViewer, content, rceVersion, theme: externalTheme = {}, getStyle: _getStyle, isCompact, appSettings, section, isBusinessManager, isSeo, isMobile, locale, isPostPageBundle, instance, postId, parentClass, setViewerRef, baseUrl, iframeSandboxDomain, shouldApplyPostDesignInFeed, } = this.props;
        const isPostPage = isPostPageBundle;
        const plugins = this.getPlugins();
        const theme = createRicosTheme({
            appSettings,
            isPostPage,
            isMobile,
            section,
            parentClass,
            shouldApplyPostDesignInFeed,
        });
        const style = _getStyle(appSettings);
        const className = classNames(styles.container);
        return (React.createElement("div", { className: styles.relativeContainer },
            React.createElement("div", { style: style, ref: setViewerRef, className: className, "data-rce-version": rceVersion },
                React.createElement(WixRicosViewer, { plugins: plugins, content: content, theme: { ...theme, ...externalTheme }, locale: locale, instance: instance, onError: this.onError, isMobile: isMobile, seoSettings: isSeo, linkSettings: linkSettings, wixExperiments: this.wixExperiments, addAnchors: true, baseUrl: baseUrl, iframeSandboxDomain: iframeSandboxDomain, biSettings: {
                        consumer: 'Blog',
                        contentId: postId,
                        platform: isBusinessManager ? 'BM' : 'Livesite',
                        usage: isCompact ? 'Comment' : isPostPage ? 'Post' : section,
                    } }))));
    }
}
RicosViewer.propTypes = {
    WixRicosViewer: PropTypes.elementType.isRequired,
    pluginAudio: PropTypes.func.isRequired,
    pluginCodeBlock: PropTypes.func.isRequired,
    pluginDivider: PropTypes.func.isRequired,
    pluginLinkButton: PropTypes.func.isRequired,
    pluginGallery: PropTypes.func.isRequired,
    pluginGiphy: PropTypes.func.isRequired,
    pluginHashtag: PropTypes.func.isRequired,
    pluginHtml: PropTypes.func.isRequired,
    pluginImage: PropTypes.func.isRequired,
    pluginLineSpacing: PropTypes.func.isRequired,
    pluginIndent: PropTypes.func.isRequired,
    pluginLink: PropTypes.func.isRequired,
    pluginLinkPreview: PropTypes.func.isRequired,
    pluginTextColor: PropTypes.func.isRequired,
    pluginTextHighlight: PropTypes.func.isRequired,
    pluginVideo: PropTypes.func.isRequired,
    pluginFileUpload: PropTypes.func.isRequired,
    pluginActionButton: PropTypes.func.isRequired,
    pluginVerticalEmbed: PropTypes.func.isRequired,
    pluginTable: PropTypes.func.isRequired,
    pluginCollapsibleList: PropTypes.func.isRequired,
    pluginPoll: PropTypes.func,
    postId: PropTypes.string.isRequired,
    content: PropTypes.object.isRequired,
    rceVersion: PropTypes.string.isRequired,
    parentClass: PropTypes.string,
    theme: PropTypes.object,
    getStyle: PropTypes.func.isRequired,
    isBusinessManager: PropTypes.bool,
    isCompact: PropTypes.bool.isRequired,
    appSettings: PropTypes.object.isRequired,
    isSeo: PropTypes.bool.isRequired,
    isInEditor: PropTypes.bool,
    isMobile: PropTypes.bool.isRequired,
    locale: PropTypes.string.isRequired,
    isPostPageBundle: PropTypes.bool.isRequired,
    instance: PropTypes.string.isRequired,
    metasiteId: PropTypes.string.isRequired,
    pageUrl: PropTypes.string.isRequired,
    isHexColor: PropTypes.func.isRequired,
    hashtagNavigate: PropTypes.func.isRequired,
    baseUrl: PropTypes.string,
    section: PropTypes.string.isRequired,
    sectionUrl: PropTypes.string.isRequired,
    onButtonClick: PropTypes.func,
    disableTheme: PropTypes.bool,
    isHashtagPageAsFeedPageEnabled: PropTypes.bool,
    isRicosEmbedButtonsEnabled: PropTypes.bool,
    Wix: PropTypes.object.isRequired,
    experiments: PropTypes.object.isRequired,
    setViewerRef: PropTypes.func.isRequired,
    isDemoPost: PropTypes.bool,
    shouldApplyPostDesignInFeed: PropTypes.bool,
    resolveFileUrl: PropTypes.func.isRequired,
};
RicosViewer.defaultProps = {
    isCompact: false,
    isBusinessManager: false,
    fallback: () => 'Content cannot be displayed',
    getStyle,
};
export default RicosViewer;
//# sourceMappingURL=ricos-viewer.js.map